import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ToastService } from 'services/toast.service';
import { NavigationService } from '../../../services/navigation.service';

import { ClientService } from '../../../services/client.service';
import { SiteService } from '../../../services/site.service';
import { EquipmentService } from '../../../services/equipment.service';

import { Client, RootState, Site, Option, CommonGlobalService, Ability, AbilityFilter, Skill, SkillFilter, UUID, Sector } from '@umanforge/common';
import { AbilityOfEquipmentLinked, SkillOfEquipmentLinked } from '@umanforge/common/dist/interfaces/equipment.interfaces';
import { PostEquipment } from 'interfaces/api_equipment.interfaces';
import { PostClient } from 'interfaces/api_client.interfaces';
import { PostSite } from 'interfaces/api_site.interfaces';
import { PostSector } from 'interfaces/api_sector.interfaces';

import { DEFAULT_POST_CLIENT, getCreateClient } from '../../../constants/client.constants';
import { DEFAULT_POST_SITE, getCreateSite } from '../../../constants/site.constants';
import { DEFAULT_POST_SECTOR, getCreateSector } from '../../../constants/sector.constants';
import { DEFAULT_POST_EQUIPMENT } from '../../../constants/equipment.constants';
import { AbilityService } from 'services/ability.service';
import { SkillService } from 'services/skill.service';
import { SectorService } from 'services/sector.service';

const toastService: ToastService = new ToastService( 'Create Equipment' );

export function useCreateEquipment() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const createNewClient: Client = getCreateClient( t );
  const createNewSite: Site = getCreateSite( t );
  const createNewSector: Sector = getCreateSector( t );

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ postEquipment, setPostEquipment ] = useState<PostEquipment>( structuredClone( DEFAULT_POST_EQUIPMENT ) );
  const [ postSector, setPostSector ] = useState<PostSector>( structuredClone( DEFAULT_POST_SECTOR ) );
  const [ postSite, setPostSite ] = useState<PostSite>( structuredClone( DEFAULT_POST_SITE ) );
  const [ postClient, setPostClient ] = useState<PostClient>( structuredClone( DEFAULT_POST_CLIENT ) );

  const [ selectedClient, setSelectedClient ] = useState<Option<Client> | undefined>();
  const [ selectedSite, setSelectedSite ] = useState<Option<Site> | undefined>();
  const [ selectedSector, setSelectedSector ] = useState<Option<Sector> | undefined>();

  const [ selectedAbilities, setSelectedAbilities ] = useState<Option<AbilityOfEquipmentLinked>[]>( [] );
  const [ selectedSkills, setSelectedSkills ] = useState<Option<SkillOfEquipmentLinked>[]>( [] );

  const [ allowedClients, setAllowedClients ] = useState<Option<Client>[]>( [] );
  const [ allowedSites, setAllowedSites ] = useState<Option<Site>[]>( [] );
  const [ allowedSectors, setAllowedSectors ] = useState<Option<Sector>[]>( [] );

  const [ rerenderClient, setRerenderClient ] = useState<number>( 0 );
  const [ rerenderSite, setRerenderSite ] = useState<number>( 0 );
  const [ rerenderSector, setRerenderSector ] = useState<number>( 0 );

  const [ rerenderAbility, setRerenderAbility ] = useState<number>( 0 );
  const [ rerenderSkill, setRerenderSkill ] = useState<number>( 0 );

  const [ canPost, setCanPost ] = useState<boolean>( false );
  const [ hasChange, setHasChange ] = useState<boolean>( false );
  const [ needCreateNewSite, setNeedCreateNewSite ] = useState<boolean>( false );
  const [ needCreateNewSector, setNeedCreateNewSector ] = useState<boolean>( false );
  const [ needCreateNewClient, setNeedCreateNewClient ] = useState<boolean>( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me && !( CommonGlobalService.isClientAdmin( me ) || CommonGlobalService.isSiteAdmin( me ) ) ) {
      NavigationService.toAbilities( navigate, false );
    }
    setIsLoading( true );
    refreshClients();
  }, [] );

  useEffect( () => {
    checkCanPostClient();
  }, [ postEquipment, postSite, postClient, postSector, selectedClient, selectedSite, selectedSector ] );

  useEffect( () => {
    checkHasChangeClient();
  }, [ postEquipment, postSite, postClient, postSector, selectedClient, selectedSite, selectedSector, selectedAbilities, selectedSkills ] );

  useEffect( () => {
    setRerenderClient( rerenderClient + 1 );
  }, [ allowedClients ] );

  useEffect( () => {
    setRerenderSite( rerenderSite + 1 );
  }, [ allowedSites ] );

  useEffect( () => {
    setRerenderSector( rerenderSector + 1 );
  }, [ allowedSectors ] );

  useEffect( () => {
    if ( selectedClient != null ) {
      refreshSites( selectedClient.object.id );
    }
    postSite.clientId = selectedClient?.value || '';
    setNeedCreateNewClient( selectedClient?.object.id === createNewClient.id );
    setRerenderAbility( rerenderAbility + 1 );
    setRerenderSkill( rerenderSkill + 1 );
    setRerenderSite( rerenderSite + 1 );
  }, [ selectedClient ] );

  useEffect( () => {
    if ( selectedSite != null ) {
      refreshSectors( selectedSite.object.id );
    }
    setSelectedAbilities( [] );
    setSelectedSkills( [] );
    postEquipment.sectorId = selectedSector?.value || '';
    setNeedCreateNewSite(  selectedClient != null && selectedSite?.object.id === createNewSite.id );
    setPostEquipment( structuredClone( postEquipment ) );
    if ( selectedSite?.object.id === createNewSite.id ) {
      setSelectedAbilities( [] );
      setSelectedSkills( [] );
    }
  }, [ selectedSite ] );

  useEffect( () => {
    postEquipment.sectorId = selectedSector?.value || '';
    setNeedCreateNewSector(  selectedSite != null && selectedSector?.object.id === createNewSector.id );
    setPostEquipment( structuredClone( postEquipment ) );
  }, [ selectedSector ] );

  useEffect( () => {
    postEquipment.abilitiesId = selectedAbilities.map( ability => { return ability.object.id; } );
    setPostEquipment( structuredClone( postEquipment ) );
    setRerenderAbility( rerenderAbility + 1 );
  }, [ selectedAbilities ] );

  useEffect( () => {
    postEquipment.skills = selectedSkills.map( skill => { return { id: skill.object.id, value: skill.object.value }; } );
    setPostEquipment( structuredClone( postEquipment ) );
    setRerenderSkill( rerenderSkill + 1 );
  }, [ selectedSkills ] );

  /*---------------------------- Functions ------------------------------*/
  const refreshClients = async (): Promise<void> => {
    setIsLoading( true );
    const response = await ClientService.getAllowedClients( null, token, dispatch );
    if ( response != null ) {
      if ( me?.isSuperAdmin ) {
        response.unshift( createNewClient );
      }
      const clients = CommonGlobalService.toOptions( response )
        .sort( client => { return client.object.id < createNewClient.id ? 1 : -1; } );
      setAllowedClients( clients );
      if ( clients.length === 1 ) {
        setSelectedClient( clients[ 0 ] );
      }
    }
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };

  const refreshSites = async ( clientId: string ): Promise<void> => {
    let response: Site[] = [];
    if ( clientId != null && clientId != '' && clientId != createNewClient.id ) {
      response = await SiteService.getAllowedSitesByClientsId( [ clientId ], token, dispatch );
    }
    const sitesAdmin = response.filter( site => {
      return ( me && ( CommonGlobalService.isAdminOfSite( me, site.id ) || CommonGlobalService.isAdminOfClient( me, site.clientId ) ) );
    } );
    if ( selectedSite?.object.id != clientId ) {
      setSelectedSite( undefined );
    }
    if ( sitesAdmin != null ) {
      if ( me?.isSuperAdmin ) {
        sitesAdmin.unshift( createNewSite );
      }

      const sites = CommonGlobalService.toOptions( sitesAdmin )
        .sort( site => { return site.object.id < createNewSite.id ? 1 : -1; } );
      setAllowedSites( sites );
      if ( sites.length === 1 ) {
        setSelectedSite( sites[ 0 ] );
      }
    }
  };

  const refreshSectors = async ( siteId: string ): Promise<void> => {
    let response: Sector[] = [];
    const siteParent = allowedSites.find( site => { return site.value == siteId; } );
    if ( siteId != null && siteId != '' && siteId != createNewSite.id && siteParent != null ) {
      response = await SectorService.getAllowedSectorsBySitesId( [ siteId ], token, dispatch );
    }

    const sectorAdmin = response.filter( sector => {
      return ( me && ( CommonGlobalService.isAdminOfSector( me, sector.id ) || CommonGlobalService.isAdminOfClient( me, siteParent?.object.clientId ?? '' ) || CommonGlobalService.isAdminOfSite( me, sector.siteId ) ) );
    } );
    if ( selectedSite?.object.id != siteId ) {
      setSelectedSite( undefined );
    }
    if ( sectorAdmin != null ) {
      if ( me?.isSuperAdmin ) {
        sectorAdmin.unshift( createNewSector );
      }

      const sectors = CommonGlobalService.toOptions( sectorAdmin )
        .sort( sector => { return sector.object.id < createNewSector.id ? 1 : -1; } );
      setAllowedSectors( sectors );
      if ( sectors.length === 1 ) {
        setSelectedSector( sectors[ 0 ] );
      }
    }
  };

  const resetForm = () => {
    setSelectedAbilities( [] );
    setSelectedSkills( [] );
    setSelectedSite( allowedSites.length === ( me && me.isSuperAdmin ? 2 : 1 ) ? allowedSites[ 0 ] : undefined );
    setSelectedClient( allowedClients.length === ( me && me.isSuperAdmin ? 2 : 1 ) ? allowedClients[ 0 ] : undefined );
    setPostEquipment( { ...structuredClone( DEFAULT_POST_EQUIPMENT ) } );
  };

  const checkCanPostClient = () => {
    const clientChange = (
      ( selectedClient?.object.id == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
          ( selectedClient?.object.id != null && selectedClient?.object.id != createNewClient.id )
    );
    const siteChange = (
      ( selectedSite?.object.id == createNewSite.id && postSite.name != DEFAULT_POST_SITE.name ) ||
          ( selectedSite?.object.id != createNewSite.id && selectedSite?.object.id != null && selectedSite?.object.id != '' )
    );
    const sectorChange = (
      ( postEquipment.sectorId == createNewSector.id && postSite.name != DEFAULT_POST_SECTOR.name ) ||
          ( postEquipment.sectorId != createNewSector.id && postEquipment.sectorId != null && postEquipment.sectorId != '' )
    );
    let can = false;
    const nameChange = postEquipment.name != null && postEquipment.name != '';
    const descChange = postEquipment.description != null && postEquipment.description != '';
    can = clientChange && siteChange && nameChange && sectorChange && descChange;
    setCanPost( can );
  };

  const checkHasChangeClient = () => {
    const clientChange = (
      ( selectedClient?.object.id == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
        ( selectedClient?.object.id != null && selectedClient?.object.id != createNewClient.id && allowedClients.length > 1 )
    );
    const siteChange = (
      ( selectedSite?.object.id == createNewSite.id && postSite.name != DEFAULT_POST_SITE.name ) ||
        ( selectedSite?.object.id != createNewSite.id && selectedSite?.object.id != null && selectedSite?.object.id != '' && allowedSites.length > 1 )
    );
    const sectorChange = (
      ( postEquipment.sectorId == createNewSector.id && postSector.name != DEFAULT_POST_SECTOR.name ) ||
        ( postEquipment.sectorId != createNewSector.id && postEquipment.sectorId != null && postEquipment.sectorId != '' && allowedSectors.length > 1 )
    );
    const nameChange = postEquipment.name != null && postEquipment.name != '';
    const descChange = postEquipment.description != null && postEquipment.description != '';
    setHasChange( clientChange || siteChange || nameChange || sectorChange || descChange );
  };

  /*------------------------- Requetes API User -------------------------*/

  const getAbilities = async ( filter: AbilityFilter ): Promise<{ total: number, list: Option<AbilityOfEquipmentLinked>[] }> => {
    if ( selectedSite && selectedSite.object.id !== createNewSite.id ) {
      if ( filter.filterName === undefined || filter.filterName === '' ) {
        delete filter.filterName;
      }
      if ( selectedSite != null ) {
        filter.filterSites = [ selectedSite.value ];
      }

      const abilityFilter = {
        filterName: filter.filterName,
        filterClients: filter.filterClients,
        filterSites: filter.filterSites
      };

      const result: Ability[] = ( await AbilityService.getAllowAbilities( abilityFilter as AbilityFilter, token, dispatch ) );
      return { total: result.length, list: CommonGlobalService.toOptions<AbilityOfEquipmentLinked>(
        result.map<AbilityOfEquipmentLinked>( ability => {
          return {
            id: ability.id,
            name: ability.name,
            disable: false,
            color: ability.color
          };
        } ) )
      };
    }
    return { total: 0, list: [] };
  };

  const getSkills = async ( filter: SkillFilter ): Promise<{ total: number, list: Option<SkillOfEquipmentLinked>[] }> => {
    if ( selectedSite && selectedSite.object.id !== createNewSite.id ) {
      if ( filter.filterName === undefined || filter.filterName === '' ) {
        delete filter.filterName;
      }
      if ( selectedSite != null ) {
        filter.filterSites = [ selectedSite.value ];
      }

      const skillFilter = {
        filterName: filter.filterName,
        filterClients: filter.filterClients,
        filterSites: filter.filterSites
      };

      const result: Skill[] = ( await SkillService.getAllowSkills( skillFilter as SkillFilter, token, dispatch ) );
      return { total: result.length, list: CommonGlobalService.toOptions<SkillOfEquipmentLinked>(
        result.map<SkillOfEquipmentLinked>( skill => {
          return {
            id: skill.id,
            name: skill.name,
            value: skill.value ?? 1,
            disable: false,
            color: skill.color
          };
        } ) )
      };
    }
    return { total: 0, list: [] };
  };

  const addAbilityToEquipment = ( equipmentId: UUID ): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( equipmentId && selectedAbilities.length > 0 ) {
        const body = { abilitiesId: selectedAbilities.map( ability => { return ability.object.id; } ) };
        isUpdated = await EquipmentService.postAbilitiesToEquipment( equipmentId, body, token, dispatch );
      }
      resolve( isUpdated );
    } );
  };

  const addSkillToEquipment = ( equipmentId: UUID ): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( equipmentId && selectedSkills.length > 0 ) {
        const body = { skills: selectedSkills.map( skill => { return { id: skill.object.id, value: skill.object.value }; } ) };
        isUpdated = await EquipmentService.postSkillsToEquipment( equipmentId, body, token, dispatch );
      }
      resolve( isUpdated );
    } );
  };

  /*------------------------------ Events -------------------------------*/
  const onCreateClick = async () => {
    setIsLoading( true );

    if ( postSite.clientId === createNewClient.id ) {
      const newClientId = await ClientService.postClient( postClient, token, dispatch );
      if ( newClientId ) {
        postSite.clientId = newClientId;
      }
    }
    if ( postSector.siteId === createNewSite.id ) {
      const newSiteId = await SiteService.postSite( postSite, token, dispatch );
      if ( newSiteId ) {
        postSector.siteId = newSiteId;
      }
    }
    if ( postEquipment.sectorId === createNewSector.id ) {
      const newSectorId = await SectorService.postSector( postSector, token, dispatch );
      if ( newSectorId ) {
        postEquipment.sectorId = newSectorId;
      }
    }


    if ( postEquipment.sectorId !== createNewSector.id ) {
      const equipmentId = await EquipmentService.postEquipment( postEquipment, token, dispatch );

      if ( equipmentId ) {
        await addAbilityToEquipment( equipmentId );
        await addSkillToEquipment( equipmentId );

        toastService.showSuccess( t( 'the_equipment_has_been_create' ) );
        resetForm();
        window.location.reload();
      }
    }
    setIsLoading( false );
  };

  return {
    postEquipment, setPostEquipment,
    postSector, setPostSector,
    postSite, setPostSite,
    postClient, setPostClient,
    selectedClient, setSelectedClient,
    selectedSite, setSelectedSite,
    selectedSector, setSelectedSector,
    allowedClients, setAllowedClients,
    allowedSites, setAllowedSites,
    allowedSectors, setAllowedSectors,
    rerenderClient, setRerenderClient,
    rerenderSite, setRerenderSite,
    rerenderSector, setRerenderSector,
    rerenderAbility, setRerenderAbility,
    rerenderSkill, setRerenderSkill,
    canPost, setCanPost,
    hasChange, setHasChange,
    needCreateNewSector, setNeedCreateNewSector,
    needCreateNewSite, setNeedCreateNewSite,
    needCreateNewClient, setNeedCreateNewClient,
    isLoading, setIsLoading,
    onCreateClick,
    createNewSite,
    createNewSector,

    selectedAbilities, setSelectedAbilities,
    getAbilities,

    selectedSkills, setSelectedSkills,
    getSkills
  };
}